import { useCallback, useMemo, useRef, useState } from 'react';
import type Highcharts from 'highcharts';

const ONE_MINUTE_MS = 60 * 1000;
const HALF_MINUTE_MS = 30 * 1000;
const MIN_RANGE_MS = 60 * 1000; // 1 minute minimum selection

/** Extended selection event with properties available at runtime but missing from Highcharts types */
interface SelectEvent extends Highcharts.SelectEventObject {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
}

export function roundToNearestMinute(timestamp: number): number {
  const remainder = timestamp % ONE_MINUTE_MS;
  if (remainder >= HALF_MINUTE_MS) {
    return Math.ceil(timestamp / ONE_MINUTE_MS) * ONE_MINUTE_MS;
  }
  return Math.floor(timestamp / ONE_MINUTE_MS) * ONE_MINUTE_MS;
}

interface UseDragToZoomOptions {
  /** Whether drag-to-zoom is enabled (false for non-time-series or shared users) */
  enabled: boolean;
  /** Called when the user confirms "Zoom to Selection" — receives epoch seconds (start, end) */
  onZoom: (start: number, end: number) => void;
}

export interface SelectionState {
  min: number; // ms timestamp
  max: number; // ms timestamp
  popoverX: number;
  popoverY: number;
  rectX: number;
  rectY: number;
  rectWidth: number;
  rectHeight: number;
}

interface UseDragToZoomResult {
  /** Highcharts options to merge into the chart config (adds zooming + selection handler) */
  chartOverrides: Highcharts.Options | undefined;
  /** Current selection state for rendering the popover (null when no selection) */
  selection: SelectionState | null;
  /** Attach to the container element wrapping the chart (used for mouse position tracking) */
  containerRef: React.RefCallback<HTMLElement>;
  /** Call to confirm zoom (triggers onZoom and clears selection) */
  confirmZoom: () => void;
  /** Call to dismiss the selection without zooming */
  dismissSelection: () => void;
}

export function useDragToZoom({ enabled, onZoom }: UseDragToZoomOptions): UseDragToZoomResult {
  const [selection, setSelection] = useState<SelectionState | null>(null);
  const selectionRef = useRef(selection);
  selectionRef.current = selection;
  const onZoomRef = useRef(onZoom);
  onZoomRef.current = onZoom;

  // Ref to the container element (used to compute mouse position relative to it)
  const containerElRef = useRef<HTMLElement | null>(null);

  const containerRef = useCallback((el: HTMLElement | null) => {
    containerElRef.current = el;
  }, []);

  const handleSelection = useCallback(function handleChartSelection(
    this: Highcharts.Chart,
    event: SelectEvent
  ): boolean {
    if (!event.xAxis) {
      return false;
    }

    let { min, max } = event.xAxis[0];

    // Enforce minimum range (1 minute)
    if (max - min < MIN_RANGE_MS) {
      const overage = MIN_RANGE_MS - (max - min);
      min -= overage / 2;
      max += overage / 2;
    }

    min = roundToNearestMinute(min);
    max = roundToNearestMinute(max);

    // Safeguard: if rounding caused min and max to converge, push max forward
    if (max <= min) {
      max = min + ONE_MINUTE_MS;
    }

    // event.x/y/width/height are the selection marker coordinates,
    // relative to the Highcharts chart container (already include plotLeft/plotTop).
    const { x = 0, y = 0, width = 0, height = 0 } = event;

    // Compute the offset between the Highcharts container and our positioned
    // Flex container (accounts for Flex padding).
    let offsetX = 0;
    let offsetY = 0;
    const container = containerElRef.current;
    const containerRect = container?.getBoundingClientRect();
    if (containerRect && this.container) {
      const chartRect = this.container.getBoundingClientRect();
      offsetX = chartRect.left - containerRect.left;
      offsetY = chartRect.top - containerRect.top;
    }

    const rectX = offsetX + x;
    const rectY = offsetY + y;
    const popoverX = rectX + width;

    // Use the mouse Y position from the original event (where the user released)
    // rather than the selection rect midpoint, since x-only zoom spans the full height.
    let popoverY: number;
    const mouseEvent = event.originalEvent as MouseEvent | undefined;
    if (mouseEvent && containerRect) {
      popoverY = mouseEvent.clientY - containerRect.top;
    } else {
      popoverY = rectY + height / 2;
    }

    setSelection({
      min,
      max,
      popoverX,
      popoverY,
      rectX,
      rectY,
      rectWidth: width,
      rectHeight: height
    });

    // Return false to prevent Highcharts native zoom behavior
    return false;
  }, []);

  const confirmZoom = useCallback(() => {
    const { current } = selectionRef;
    if (current) {
      const start = Math.floor(current.min / 1000);
      const end = Math.floor(Math.min(current.max, Date.now()) / 1000);
      onZoomRef.current(start, end);
    }
    setSelection(null);
  }, []);

  const dismissSelection = useCallback(() => {
    setSelection(null);
  }, []);

  const chartOverrides = useMemo((): Highcharts.Options | undefined => {
    if (!enabled) {
      return undefined;
    }
    return {
      chart: {
        zooming: { type: 'x' },
        events: {
          selection: handleSelection as Highcharts.ChartEventsOptions['selection']
        }
      },
      xAxis: {
        minRange: MIN_RANGE_MS
      }
    };
  }, [enabled, handleSelection]);

  return { chartOverrides, selection, containerRef, confirmZoom, dismissSelection };
}
